import React, { useState, useCallback, useEffect, useMemo, memo } from 'react';
import { Brain, Settings, TrendingUp, TrendingDown, Play, Pause, ChevronsUpDown } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { usePositionContext } from '../../contexts/PositionContext';
import { useAITrading } from '../../contexts/AITradingContext';
import { useAIStrategyPersistenceContext } from '../../contexts/AIStrategyPersistenceContext';
import { useBackendAITrading } from '../../contexts/BackendAITradingContext';
import { useUnifiedAITrading } from '../../hooks/useUnifiedAITrading';
import AITradingSettings from './AITradingSettings';

interface AITradePanelProps {
  symbol: string;
  currentPrice?: string;
  onTradeExecuted?: () => void;
}

const AITradePanel: React.FC<AITradePanelProps> = ({ symbol, currentPrice, onTradeExecuted }) => {
  const { theme } = useTheme();
  const { openPositions, closePosition } = usePositionContext();
  const { 
    updateSignal, 
    updateConfig,
    updateStrategy,
    executeAITrade,
    closeAllAIPositions,
    registerScrollCallback,
    unregisterScrollCallback
  } = useAITrading();
  const { isStrategyActive, getStrategyForSymbol, deactivateStrategy, saveStrategy } = useAIStrategyPersistenceContext();
  
  // Use unified AI trading hook
  const [showSettings, setShowSettings] = useState(false);
  const [isAILoading, setIsAILoading] = useState(false);
  
  const {
    aiState,
    handlers,
    currentFirestoreStrategy: aiCurrentFirestoreStrategy,
    currentAssetConfig: aiCurrentAssetConfig,
    assetAIConfig,
    refreshTrigger
  } = useUnifiedAITrading(
    symbol,
    () => setShowSettings(true),
    () => setShowSettings(false)
  );
  
  // Debug logging for settings state
  useEffect(() => {
    if (showSettings) {
      // Settings opened - no logging needed
    }
  }, [showSettings, symbol, assetAIConfig, aiCurrentFirestoreStrategy, aiCurrentAssetConfig]);
  
  // Debug logging for refreshTrigger
  // No logging needed
  
  // Use the same logic as TradingChart for consistency
  // For backward compatibility, also check local running state
  const isAIRunning = aiState.isRunning || aiCurrentAssetConfig.isRunning;

  // Calculate current AI signal state based on positions and market conditions
  const getCurrentAISignal = useCallback(() => {
    // Check if AI is running
    if (!isAIRunning) {
      return null;
    }

    // Check for existing AI positions for this symbol
    const existingAIPositions = openPositions.filter(position => 
      position.isAI && position.symbol === symbol
    );

    // If there's already an AI position, return the position type as the active signal
    if (existingAIPositions.length > 0) {
      const position = existingAIPositions[0]; // Get the first (and only) AI position
      const signal = position.type.toLowerCase() as 'buy' | 'sell'; // Convert 'Buy'/'Sell' to 'buy'/'sell'
      // console.log(`🎯 AI Signal: Position exists - ${position.type} -> ${signal} for ${symbol}`);
      return signal;
    }

    // Return the current signal from AI config when no position exists
    return aiCurrentAssetConfig.signal;
  }, [isAIRunning, openPositions, symbol, aiCurrentAssetConfig.signal]);
  
  // Debug logging to help troubleshoot button state
  // console.log(`🔍 AITradePanel: Button state for ${symbol}`, {
  //   aiStateIsRunning: aiState.isRunning,
  //   currentFirestoreStrategyIsActive: (currentFirestoreStrategy as any)?.isActive,
  //   isAIRunning,
  //   refreshTrigger,
  //   currentFirestoreStrategy: currentFirestoreStrategy
  // });
  
  // Force re-render when refreshTrigger changes
  useEffect(() => {
    // console.log(`🔄 AITradePanel: Refresh trigger changed to ${refreshTrigger} for ${symbol}`);
  }, [refreshTrigger, symbol]);

  // Handle AI Start/Stop with loading state
  const handleAIStartStop = useCallback(async () => {
    if (isAILoading) return; // Prevent multiple clicks
    
    setIsAILoading(true);
    try {
      if (isAIRunning) {
        await handlers.handleStopAI();
      } else {
        await handlers.handleStartAI();
      }
    } catch (error) {
      console.error('Error handling AI start/stop:', error);
    } finally {
      setIsAILoading(false);
    }
  }, [isAILoading, isAIRunning, handlers]);

  // Register scroll callback when component mounts
  useEffect(() => {
    if (onTradeExecuted) {
      registerScrollCallback(onTradeExecuted);
    }
    
    return () => {
      unregisterScrollCallback();
    };
  }, [onTradeExecuted, registerScrollCallback, unregisterScrollCallback]);

  const handleSignalUpdate = useCallback((signal: 'buy' | 'sell' | null) => {
    updateSignal(symbol, signal);
  }, [symbol, updateSignal]);

  const handleConfigUpdate = useCallback((config: any) => {
    updateConfig(symbol, config);
  }, [symbol, updateConfig]);

  /**
   * Handle strategy change for the current asset (updated for per-asset system)
   */
  const handleStrategyChange = useCallback(async (strategyId: string) => {
    try {
      // If strategyId is empty, it means we're removing the strategy
      if (!strategyId) {
        await handlers.handleRemoveStrategy();
        return;
      }
      
      // Just update the legacy strategy for selection (don't auto-save)
      // This allows the popup to show Save/Remove buttons like TradingChart
      updateStrategy(symbol, { strategy: strategyId });
          
        } catch (error) {
      console.error(`❌ Error handling strategy change for ${symbol}:`, error);
    }
  }, [symbol, updateStrategy, handlers]);

  // Note: Signal generation is now handled by the global AI trading service
  // This component only displays the current signal and manages start/stop

  return (
    <div className="space-y-4">
      {/* AI Trade Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Brain className="h-4 w-4 text-[#2AB7A6]" />
          <span className="text-xs font-medium theme-text-primary">AI Trading</span>
        </div>
        <button 
          onClick={handlers.handleShowSettings}
          className="p-1 text-gray-400 hover:text-gray-300 transition-colors duration-200"
        >
          <Settings className="h-4 w-4" />
        </button>
      </div>

      {/* Current Strategy Display */}
      {!aiCurrentAssetConfig.showMonitoringMessage &&
      <div className="p-3 bg-gray-500/10 rounded-lg">
        <div className="text-xs theme-text-quaternary mb-1">
          {(aiState.canStart || aiState.isRunning) || ((aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled && (aiCurrentFirestoreStrategy as any)?.activeStrategyInstance !== null) ? 'Active Strategy:' : 'Strategy Status:'}
        </div>
        <div className="text-xs font-medium theme-text-quaternary">
          {(aiState.canStart || aiState.isRunning) || ((aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled && (aiCurrentFirestoreStrategy as any)?.activeStrategyInstance !== null) ? (
            (aiCurrentFirestoreStrategy as any)?.aiSelectedStrategy || 
            (aiCurrentAssetConfig.strategy === 'stochastic' ? 'MiAI' :
             aiCurrentAssetConfig.strategy === 'supertrend' ? 'LazyB CrazyB' : 
             aiCurrentAssetConfig.strategy === 'bollingerbands' ? 'Huge Hug' :
             aiCurrentAssetConfig.strategy === 'rsi' ? 'AI Assistant' : 
             (aiCurrentFirestoreStrategy as any)?.name || 'LazyB CrazyB')
          ) : (
            <span className="theme-text-quaternary">No Strategy selected</span>
          )}
        </div>
      </div>
      }
      {/* AI Control Button - Exact copy of TradingChart button */}
      <button
        data-ai-button
        onClick={handleAIStartStop}
        disabled={isAILoading}
        className={`
          w-full flex items-center justify-center space-x-2 py-3 px-4 rounded-lg text-sm font-medium transition-colors duration-200
          ${isAILoading 
            ? 'opacity-50 cursor-not-allowed' 
            : isAIRunning 
              ? (theme === 'dark' 
                  ? 'bg-red-500/30 text-red-300 hover:bg-red-500/40' 
                  : 'bg-red-100 text-red-700 hover:bg-red-200')
              : (aiState.canStart || (aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled)
                ? (theme === 'dark' 
                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                    : 'bg-green-50 text-green-600 hover:bg-green-100')
                : (theme === 'dark' 
                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                    : 'bg-green-50 text-green-600 hover:bg-green-100')
          }
        `}
        title={
          isAILoading 
            ? (isAIRunning ? 'Stopping AI...' : 'Starting AI...')
            : isAIRunning 
              ? 'Stop AI Trading' 
              : ((aiState.canStart || ((aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled && (aiCurrentFirestoreStrategy as any)?.activeStrategyInstance !== null))
                  ? 'Start AI Trading' 
                  : 'Select AI Strategy')
        }
      >
        {isAILoading ? (
          <>
            <div className="w-4 h-4 border border-current border-t-transparent rounded-full animate-spin"></div>
            <span>{isAIRunning ? 'Stopping...' : 'Starting...'}</span>
          </>
        ) : (aiState.canStart || aiState.isRunning || ((aiCurrentFirestoreStrategy as any)?.aiTradeStartEnabled && (aiCurrentFirestoreStrategy as any)?.activeStrategyInstance !== null)) ? (
          isAIRunning ? (
            <>
              <Pause className="h-4 w-4" />
              <span>{isAIRunning ? 'Stop AI' : 'Start AI'}</span>
            </>
          ) : (
            <>
              <Play className="h-4 w-4" />
              <span>{isAIRunning ? 'Stop AI' : 'Start AI'}</span>
            </>
          )
        ) : (
          <span>{isAIRunning ? 'Stop AI' : 'Select Strategy'}</span>
        )}
      </button>

      {/* AI Status Indicator - Show when Stop AI button is active */}
      {isAIRunning && (
        <div className="p-2 bg-green-500/20 border border-green-500/30 rounded-lg">
          <div className="text-xs font-medium text-green-400 text-center py-2">
            <div className="flex justify-center mb-0">
                <svg className="h-12 w-12 animate-pulse" fill="currentColor"  viewBox="0 0 640 640"><path d="M344 88C344 74.7 333.3 64 320 64C306.7 64 296 74.7 296 88L296 128L240 128C195.8 128 160 163.8 160 208L160 232L136 232C122.7 232 112 242.7 112 256C112 269.3 122.7 280 136 280L160 280L160 304C160 348.2 195.8 384 240 384L400 384C444.2 384 480 348.2 480 304L480 280L504 280C517.3 280 528 269.3 528 256C528 242.7 517.3 232 504 232L480 232L480 208C480 163.8 444.2 128 400 128L344 128L344 88zM432 208L432 304C432 321.7 417.7 336 400 336L240 336C222.3 336 208 321.7 208 304L208 208C208 190.3 222.3 176 240 176L400 176C417.7 176 432 190.3 432 208zM144 512C144 485.5 165.5 464 192 464L448 464C474.5 464 496 485.5 496 512L496 552C496 565.3 506.7 576 520 576C533.3 576 544 565.3 544 552L544 512C544 459 501 416 448 416L192 416C139 416 96 459 96 512L96 552C96 565.3 106.7 576 120 576C133.3 576 144 565.3 144 552L144 512zM264 536C264 522.7 253.3 512 240 512C226.7 512 216 522.7 216 536L216 552C216 565.3 226.7 576 240 576C253.3 576 264 565.3 264 552L264 536zM320 512C306.7 512 296 522.7 296 536L296 552C296 565.3 306.7 576 320 576C333.3 576 344 565.3 344 552L344 536C344 522.7 333.3 512 320 512zM424 536C424 522.7 413.3 512 400 512C386.7 512 376 522.7 376 536L376 552C376 565.3 386.7 576 400 576C413.3 576 424 565.3 424 552L424 536zM304 240C304 222.3 289.7 208 272 208C254.3 208 240 222.3 240 240C240 257.7 254.3 272 272 272C289.7 272 304 257.7 304 240zM368 272C385.7 272 400 257.7 400 240C400 222.3 385.7 208 368 208C350.3 208 336 222.3 336 240C336 257.7 350.3 272 368 272z"/></svg>
            </div>
            <div className="flex justify-center mb-0 hidden">
              <div className="h-12 w-12 bg-green-500/20 rounded-full flex items-center justify-center animate-pulse">
                <div className="h-8 w-8 bg-green-500 rounded-full"></div>
              </div>
            </div>
            <div className="text-center p-4 py-1">AI is thinking  & monitoring {symbol} for signals</div>
            {aiCurrentAssetConfig.lastSignalTime && (
              <div className="text-xs text-green-600 mt-1">
                Last signal: {new Date(aiCurrentAssetConfig.lastSignalTime).toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Trade Buttons */}
      <div className="space-y-2">
        <div className="text-xs theme-text-quaternary">AI Trade Actions:</div>
        <div className="flex space-x-2">
          <button
            className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-colors duration-200 ${
              getCurrentAISignal() === 'buy'
                ? 'bg-[#2AB7A6] text-white'
                : 'bg-[#2AB7A6]/20 text-[#2AB7A6] hover:bg-[#2AB7A6]/30'
            }`}
            disabled
          >
            <TrendingUp className="h-3 w-3 inline mr-1" />
            AI Buy {getCurrentAISignal() === 'buy' && '(Active)'}
          </button>
          <button
            className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-colors duration-200 ${
              getCurrentAISignal() === 'sell'
                ? 'bg-red-500 text-white'
                : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
            }`}
            disabled
          >
            <TrendingDown className="h-3 w-3 inline mr-1" />
            AI Sell {getCurrentAISignal() === 'sell' && '(Active)'}
          </button>
        </div>
        {/* Debug info for signal state */}
        <div className="text-xs theme-text-quaternary text-center">
          Signal: {getCurrentAISignal() || 'None'} ~ Running: {isAIRunning ? 'Yes' : 'No'} 
        </div>
      </div>

      {/* AI Status */}
      <div className="text-xs theme-text-quaternary text-center " style={{ marginTop: '5px', paddingBottom: '10px' }}>
        {isAIRunning 
          ? 'AI Trading is Active' 
          : 'AI Trading is starting...'}
      </div>

      {/* Settings Popup */}
      <AITradingSettings
        isOpen={showSettings}
        onClose={handlers.handleCloseSettings}
        symbol={symbol}
        currentPrice={currentPrice}
        selectedStrategy={
          // Use new per-asset system first, fallback to legacy system
          assetAIConfig?.activeStrategyInstance?.type ||
          assetAIConfig?.activeStrategyInstance?.id ||
          (aiCurrentFirestoreStrategy as any)?.type ||
          (aiCurrentFirestoreStrategy as any)?.id ||
          aiCurrentAssetConfig.strategy ||
          ''
        }
        onStrategyChange={handleStrategyChange}
        onConfigUpdate={handleConfigUpdate}
        onSignalUpdate={handleSignalUpdate}
        isRunning={aiState.isRunning || aiCurrentAssetConfig.isRunning}
      />
    </div>
  );
};

export default memo(AITradePanel);
